import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Dialog, Input } from '@dreamtree-org/twreact-ui';
import { Plus } from 'lucide-react';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import Can from '@rbac/Can';
import { useAuth } from '@rbac/useCan';
import { adminRequest, errorOf } from '@/lib/tenantAuth';

/**
 * C06/C07 — roles and their permission matrix. The catalog comes from the server (tenants
 * cannot invent permissions); the server enforces the delegation ceiling, so a box you can
 * tick here can still be refused if you do not hold that permission yourself. Saving uses
 * the role's version, so two admins editing at once get a conflict, not a silent overwrite.
 */
function groupByResource(catalog) {
  const groups = new Map();
  for (const p of catalog) groups.set(p.resource, [...(groups.get(p.resource) || []), p]);
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

function PermissionMatrix({ catalog, selected, onToggle, disabled }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {groupByResource(catalog).map(([resource, perms]) => (
        <fieldset key={resource} className="border border-secondary-200 dark:border-secondary-700 rounded p-2">
          <legend className="px-1 text-xs font-semibold uppercase text-secondary-500">{resource}</legend>
          {perms.map((p) => (
            <label key={p.key} className="flex items-start gap-2 text-sm py-0.5">
              <input
                type="checkbox"
                className="mt-1"
                checked={selected.has(p.key)}
                disabled={disabled}
                onChange={() => onToggle(p.key)}
              />
              <span>
                <code className="text-xs">{p.action}</code>
                {p.companyOnly && <Badge size="sm" variant="secondary" className="ml-1">company-wide only</Badge>}
                <span className="block text-xs text-secondary-500">{p.label}</span>
              </span>
            </label>
          ))}
        </fieldset>
      ))}
    </div>
  );
}

function NewRoleDialog({ open, onClose, catalog, onCreated }) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [err, setErr] = useState('');
  useEffect(() => {
    if (open) { setKey(''); setName(''); setSelected(new Set()); setErr(''); }
  }, [open]);
  const toggle = (k) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await adminRequest('post', '/roles', { key, name, permissions: [...selected] });
      onCreated();
    } catch (ex) {
      setErr(errorOf(ex).message);
    }
  };
  return (
    <Dialog isOpen={open} onClose={onClose} title="New role" size="xl">
      <form className="space-y-3" onSubmit={submit}>
        {err && <Alert variant="error">{err}</Alert>}
        <div className="grid gap-2 sm:grid-cols-2">
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
          <Input label="Key" value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} placeholder="support_lead" required />
        </div>
        <PermissionMatrix catalog={catalog} selected={selected} onToggle={toggle} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit">Create role</Button>
        </div>
      </form>
    </Dialog>
  );
}

export default function RolesAdmin() {
  const auth = useAuth();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([adminRequest('get', '/roles'), adminRequest('get', '/permissions')]);
      setRoles(r);
      setCatalog(c);
    } catch (ex) {
      setErr(errorOf(ex).message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const role = useMemo(() => roles.find((r) => r.id === editing) || null, [roles, editing]);
  useEffect(() => { if (role) setDraft(new Set(role.permissions)); }, [role]);

  const toggle = (k) => setDraft((s) => {
    const next = new Set(s);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const save = async () => {
    setErr('');
    setNotice('');
    try {
      const res = await adminRequest('put', `/roles/${role.id}/permissions`, { permissions: [...draft], expectedVersion: role.version });
      setNotice(`Saved — takes effect for ${res.affectedUsers} user(s) on their next request`);
      await load();
    } catch (ex) {
      setErr(errorOf(ex).message);
    }
  };

  const locked = !role || (role.isSystem && role.key === 'tenantadmin') || !(auth.permissions || []).includes('roles:manage');

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Roles"
        subtitle="What each role may do. A role assignment decides where — company-wide or one branch."
        actions={<Can module="roles" action="manage"><Button size="sm" onClick={() => setCreating(true)}><Plus size={14} className="mr-1 inline" /> New role</Button></Can>}
      />
      {err && <Alert variant="error" dismissible onDismiss={() => setErr('')}>{err}</Alert>}
      {notice && <Alert variant="success" dismissible onDismiss={() => setNotice('')}>{notice}</Alert>}
      <div className="grid gap-3 lg:grid-cols-[18rem_1fr]">
        <Card className="p-2 space-y-1">
          {roles.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setEditing(r.id)}
              className={`w-full text-left px-2 py-1.5 rounded text-sm ${editing === r.id ? 'bg-primary-50 dark:bg-primary-900/30' : 'hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}
            >
              <div className="font-medium flex items-center gap-1">{r.name}{r.isSystem && <Badge size="sm" variant="secondary">system</Badge>}</div>
              <div className="text-xs text-secondary-500">{r.permissions.length} permissions · {r.holders} assigned</div>
            </button>
          ))}
        </Card>
        <Card className="p-3 space-y-3">
          {!role && <p className="text-sm text-secondary-500">Pick a role to see what it grants.</p>}
          {role && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-semibold">{role.name}</h2>
                <code className="text-xs">{role.key}</code>
                {role.description && <span className="text-xs text-secondary-500">{role.description}</span>}
                {!locked && <Button size="sm" className="ml-auto" onClick={save}>Save permissions</Button>}
              </div>
              {role.key === 'tenantadmin' && <Alert variant="info">The company administrator role always holds every permission.</Alert>}
              <PermissionMatrix catalog={catalog} selected={draft} onToggle={toggle} disabled={locked} />
            </>
          )}
        </Card>
      </div>
      <NewRoleDialog open={creating} onClose={() => setCreating(false)} catalog={catalog} onCreated={() => { setCreating(false); load(); }} />
    </PageShell>
  );
}
